import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  FileText, 
  Eye, 
  CheckCircle, 
  XCircle, 
  ChevronRight,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';
import { useNavigate } from 'react-router-dom';

interface FunnelData {
  enviadas: number;
  visualizadas: number;
  aprovadas: number;
  rejeitadas: number;
  valorTotal: number;
  valorAprovado: number;
}

export const ProposalFunnelWidget = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [funnel, setFunnel] = useState<FunnelData>({
    enviadas: 0,
    visualizadas: 0,
    aprovadas: 0,
    rejeitadas: 0,
    valorTotal: 0,
    valorAprovado: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFunnelData = async () => {
      if (!user?.id) return;
      
      try {
        setLoading(true);
        
        // Get proposals data
        const { data: propostas, error } = await supabase
          .from('propostas')
          .select('status, valor, data_envio')
          .neq('status', 'rascunho');

        if (error) throw error;

        const enviadas = propostas?.filter((p: any) => p.data_envio).length || 0;
        const aprovadas = propostas?.filter((p: any) => p.status === 'aprovada').length || 0;
        const rejeitadas = propostas?.filter((p: any) => p.status === 'rejeitada').length || 0;
        
        // Visualizadas: approximate as enviadas - rascunhos for now
        // In production, this would come from track-proposal-view analytics
        const visualizadas = Math.round(enviadas * 0.8); // 80% view rate estimate
        
        const valorTotal = propostas?.reduce((sum: number, p: any) => sum + Number(p.valor || 0), 0) || 0;
        const valorAprovado = propostas
          ?.filter((p: any) => p.status === 'aprovada')
          .reduce((sum: number, p: any) => sum + Number(p.valor || 0), 0) || 0;

        setFunnel({
          enviadas,
          visualizadas,
          aprovadas,
          rejeitadas,
          valorTotal,
          valorAprovado
        });
      } catch (error) {
        console.error('Error fetching funnel data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFunnelData();
  }, [user?.id]);

  const conversionRate = funnel.enviadas > 0 
    ? Math.round((funnel.aprovadas / funnel.enviadas) * 100) 
    : 0;

  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `R$ ${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `R$ ${(value / 1000).toFixed(0)}k`;
    }
    return `R$ ${value.toLocaleString('pt-BR')}`;
  };

  const funnelSteps = [
    { 
      label: 'Enviadas', 
      value: funnel.enviadas, 
      icon: FileText,
      color: 'bg-blue-500',
      width: 100
    },
    { 
      label: 'Visualizadas', 
      value: funnel.visualizadas, 
      icon: Eye,
      color: 'bg-indigo-500',
      width: funnel.enviadas > 0 ? (funnel.visualizadas / funnel.enviadas) * 100 : 0
    },
    { 
      label: 'Aprovadas', 
      value: funnel.aprovadas, 
      icon: CheckCircle,
      color: 'bg-emerald-500',
      width: funnel.enviadas > 0 ? (funnel.aprovadas / funnel.enviadas) * 100 : 0
    },
  ];

  return (
    <Card className="bento-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            Funil de Propostas
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-7 text-xs"
            onClick={() => navigate('/propostas')}
          >
            Ver todas
            <ChevronRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Conversion Rate Header */}
        <div className="flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-primary/10 to-primary/5">
          <div>
            <p className="text-xs text-muted-foreground">Taxa de Conversão</p>
            <p className="text-2xl font-bold text-foreground">{conversionRate}%</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Valor Fechado</p>
            <p className="text-lg font-semibold text-emerald-500">
              {formatCurrency(funnel.valorAprovado)}
            </p>
          </div>
          <Badge 
            variant={conversionRate >= 30 ? 'default' : 'secondary'}
            className="gap-1"
          >
            {conversionRate >= 30 ? (
              <TrendingUp className="h-3 w-3" />
            ) : (
              <TrendingDown className="h-3 w-3" />
            )}
            {conversionRate >= 30 ? 'Bom' : 'Melhorar'}
          </Badge>
        </div>

        {/* Visual Funnel */}
        <div className="space-y-2">
          {funnelSteps.map((step, index) => (
            <motion.div
              key={step.label}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="relative"
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2 text-sm">
                  <step.icon className="h-4 w-4 text-muted-foreground" />
                  <span>{step.label}</span>
                </div>
                <span className="text-sm font-semibold">{step.value}</span>
              </div>
              <div className="h-8 bg-muted/30 rounded-lg overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${step.width}%` }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className={`h-full ${step.color} rounded-lg flex items-center justify-end pr-2`}
                >
                  {step.width > 20 && (
                    <span className="text-xs font-medium text-white">
                      {Math.round(step.width)}%
                    </span>
                  )}
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Rejected indicator */}
        {funnel.rejeitadas > 0 && (
          <div className="flex items-center justify-between p-2 rounded-lg bg-destructive/10 text-destructive">
            <div className="flex items-center gap-2 text-sm">
              <XCircle className="h-4 w-4" />
              <span>Rejeitadas</span>
            </div>
            <span className="text-sm font-semibold">{funnel.rejeitadas}</span>
          </div>
        )}

        {/* Bottleneck Analysis */}
        {funnel.enviadas > 0 && (
          <div className="p-3 rounded-lg bg-muted/30 border border-border/50">
            <p className="text-xs font-medium text-foreground mb-1">
              💡 Análise do Gargalo
            </p>
            <p className="text-xs text-muted-foreground">
              {funnel.visualizadas < funnel.enviadas * 0.5 
                ? 'Baixa taxa de visualização. Revise o assunto do email e timing de envio.'
                : funnel.aprovadas < funnel.visualizadas * 0.3
                  ? 'Propostas são vistas mas não fecham. Revise preços ou escopo.'
                  : 'Funil saudável! Continue o bom trabalho.'
              }
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
